export interface Keystroke {
  readonly key: string;
  readonly ctrl?: boolean;
}

/**
 * 正規化前の入力記述。`key` は `KeyboardEvent.key` と同じ意味（Shift適用後の文字）を持つ。
 * `code` は物理キー（`KeyboardEvent.code`）。IME や配列により `key` が Unidentified になる場合の保険。
 */
export interface RawKeyInput {
  readonly key: string;
  readonly code?: string;
  readonly ctrl?: boolean;
  readonly alt?: boolean;
  readonly meta?: boolean;
  readonly isComposing?: boolean;
}

const MODIFIER_ONLY_KEYS = new Set(["Shift", "Control", "Alt", "Meta"]);

/** JIS配列でバックスラッシュの位置にある円記号（半角¥ / 全角￥）。 */
const YEN_SIGNS = new Set(["¥", "￥"]);

const SPECIAL_BY_KEY: Readonly<Record<string, string>> = {
  Escape: "<Esc>",
  Enter: "<CR>",
  Backspace: "<BS>",
  Delete: "<Del>",
};

const SPECIAL_BY_CODE: Readonly<Record<string, string>> = {
  Escape: "<Esc>",
  Enter: "<CR>",
  NumpadEnter: "<CR>",
  Backspace: "<BS>",
  Delete: "<Del>",
};

/**
 * 1打を教材・ヘルプと同じ表記（`w` / `<C-r>` / `<Space>` / `<Esc>`）へ直す。
 * 打鍵の帯の札に出す文字であると同時に、`DrillGoal.requiredCommands` の照合語彙でもある。
 */
export function keystrokeNotation(keystroke: Keystroke): string {
  if (keystroke.ctrl) {
    return `<C-${keystroke.key}>`;
  }
  if (keystroke.key === " ") {
    return "<Space>";
  }
  return keystroke.key;
}

export function isModifierOnlyKey(key: string): boolean {
  return MODIFIER_ONLY_KEYS.has(key);
}

function specialKeyNotation(input: RawKeyInput): string | null {
  const fromKey = SPECIAL_BY_KEY[input.key];
  if (fromKey) {
    return fromKey;
  }
  if (input.code) {
    return SPECIAL_BY_CODE[input.code] ?? null;
  }
  return null;
}

/**
 * KeyboardEvent相当の入力をKeystrokeへ正規化する。
 * 修飾キー単独・IME変換中・名前付き特殊キーはKeystrokeを生成しない（spec/design.md 正規化表）。
 */
export function normalizeKeystroke(input: RawKeyInput): Keystroke | null {
  if (input.isComposing || input.key === "Process") {
    return null;
  }

  if (MODIFIER_ONLY_KEYS.has(input.key)) {
    return null;
  }

  // 端末vim互換: Ctrl+[ は Esc、Ctrl+h は Backspace。
  if (input.ctrl && (input.key === "[" || input.key === "［")) {
    return { key: "<Esc>" };
  }
  if (input.ctrl && (input.key === "h" || input.key === "H")) {
    return { key: "<BS>" };
  }

  const special = specialKeyNotation(input);
  if (special) {
    return { key: special };
  }

  if (YEN_SIGNS.has(input.key)) {
    return { key: "\\" };
  }

  // ArrowLeft / Convert / F1 など、印字可能文字でない名前付きキーは捨てる。
  if (input.key.length !== 1) {
    return null;
  }

  if (input.ctrl) {
    return { key: input.key, ctrl: true };
  }

  return { key: input.key };
}
